import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Bot, Send, Camera, Paperclip, ScanSearch, User, FileText, Sparkles, Loader2 } from "lucide-react";
import { sanitizeHtml } from "../lib/sanitize";
import { toast } from "sonner";
import { guruApi } from "../lib/api";
import { GuruActionCard, GuruResultCard, GuruLinkCard } from "../components/guru/GuruActionCard";
import CommandThemeScope from "../components/CommandThemeScope";
import { useNavigationHistory } from "../contexts/NavigationHistoryContext";

let _mid = 0;
const uid = () => `mg-${Date.now()}-${_mid++}`;

const SUGGESTED = [
  "AC not cooling — where do I start?",
  "Build an estimate for this customer",
  "Book a maintenance appointment",
  "Message dispatch I need a part",
];

function formatMarkdown(t) {
  if (!t) return "";
  return t
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
}

export default function MobileGuru() {
  const navigate = useNavigate();
  const { goBack } = useNavigationHistory();
  const [messages, setMessages] = useState([
    {
      id: 1,
      sender: "bot",
      text: "Hi, I'm **Diagnostic Guru** — your AI co-pilot. Ask me to diagnose an issue, or tell me to build an estimate, email a document, book an appointment, or message a teammate. Snap a photo of a unit, gauge, or panel and I'll read it too.",
    },
  ]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [actionBusy, setActionBusy] = useState(null);
  const scrollRef = useRef(null);
  const imageInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, isTyping]);

  const send = async (text) => {
    const t = (text ?? input).trim();
    if (!t || isTyping) return;
    setMessages((m) => [...m, { id: uid(), sender: "user", text: t }]);
    setInput("");
    setIsTyping(true);
    try {
      const res = await guruApi.agent({ sessionId, text: t, useRag: true, origin: window.location.origin });
      if (res.sessionId) setSessionId(res.sessionId);
      setMessages((m) => [
        ...m,
        {
          id: uid(),
          sender: "bot",
          text: res.reply,
          sources: (res.sources || []).map((s) => s.filename),
          cards: res.cards || [],
          pendingAction: res.pendingAction || null,
        },
      ]);
    } catch {
      toast.error("Diagnostic Guru failed to respond");
      setMessages((m) => [...m, { id: uid(), sender: "bot", text: "I couldn't reach the AI service. Please try again." }]);
    } finally {
      setIsTyping(false);
    }
  };

  const confirmAction = async (msg, extraArgs = {}) => {
    const pa = msg.pendingAction;
    if (!pa) return;
    setActionBusy(msg.id);
    try {
      const res = await guruApi.agentExecute({
        sessionId,
        action: pa.action,
        args: { ...pa.args, ...extraArgs },
        origin: window.location.origin,
      });
      setMessages((m) =>
        m.map((x) => (x.id === msg.id ? { ...x, resolved: true } : x)).concat({
          id: uid(),
          sender: "bot",
          text: res.reply || (res.ok ? "Done." : res.error || "Couldn't complete that."),
          result: res.ok ? res.result : null,
        }),
      );
      if (res.ok) toast.success(res.say || "Done");
      else toast.error(res.error || "Action failed");
    } catch {
      toast.error("Action failed — please try again.");
    } finally {
      setActionBusy(null);
    }
  };

  const cancelAction = (msg) => {
    setMessages((m) =>
      m.map((x) => (x.id === msg.id ? { ...x, resolved: true } : x)).concat({
        id: uid(),
        sender: "bot",
        text: "Okay, cancelled.",
      }),
    );
  };

  const openResult = (result) => {
    if (result?.url) navigate(result.url);
  };

  const onImage = (fileList) => {
    const file = fileList?.[0];
    if (!file || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const dataUrl = ev.target.result;
      setMessages((m) => [...m, { id: uid(), sender: "user", imageUrl: dataUrl, imageName: file.name, text: "" }]);
      setAnalyzing(true);
      setIsTyping(true);
      try {
        const res = await guruApi.agent({
          sessionId,
          text: "I'm uploading an equipment photo. Identify what you see, extract any visible readings or labels, and tell me what's likely wrong.",
          imageBase64: dataUrl,
          useRag: true,
          origin: window.location.origin,
        });
        if (res.sessionId) setSessionId(res.sessionId);
        setMessages((m) => [
          ...m,
          {
            id: uid(),
            sender: "bot",
            text: res.reply,
            sources: (res.sources || []).map((s) => s.filename),
            cards: res.cards || [],
            pendingAction: res.pendingAction || null,
          },
        ]);
      } catch {
        toast.error("Vision analysis failed");
      } finally {
        setIsTyping(false);
        setAnalyzing(false);
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div
      className="cc-mobile relative w-full h-screen bg-slate-50 overflow-hidden flex flex-col"
      data-testid="mobile-guru-page"
    >
      <CommandThemeScope surface="mobile" />
      {/* Header */}
      <header className="shrink-0 flex items-center gap-2 px-2 h-14 bg-white border-b border-slate-200">
        <button
          onClick={() => goBack("/mobile")}
          data-testid="mguru-back-btn"
          className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-600"
          aria-label="Back"
        >
          <ArrowLeft className="h-5 w-5" strokeWidth={2.5} />
        </button>
        <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center">
          <Bot className="h-5 w-5 text-white" />
        </div>
        <div className="min-w-0">
          <div className="text-[15px] font-black text-[#0B1E3F] flex items-center gap-1.5">
            Diagnostic Guru
            <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-gradient-to-br from-fuchsia-500 to-blue-500 text-white">AI</span>
          </div>
          <div className="text-[11px] text-slate-400">Diagnostic assistant</div>
        </div>
      </header>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-[#FAFBFD]" data-testid="mguru-messages">
        {messages.map((m) => (
          <div key={m.id}>
            <Bubble message={m} />
            {(m.cards?.length > 0 || m.pendingAction || m.result) && (
              <div className="ml-10 mt-1">
                {(m.cards || []).map((c, i) => (
                  <GuruLinkCard key={i} card={c} />
                ))}
                {m.pendingAction && !m.resolved && (
                  <GuruActionCard
                    pending={m.pendingAction}
                    busy={actionBusy === m.id}
                    onConfirm={(extra) => confirmAction(m, extra)}
                    onCancel={() => cancelAction(m)}
                  />
                )}
                {m.result && <GuruResultCard result={m.result} onOpen={openResult} />}
              </div>
            )}
          </div>
        ))}
        {isTyping && (
          <div className="flex gap-2 items-start">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center shrink-0">
              <Bot className="h-4 w-4 text-white" />
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-3 py-2.5">
              {analyzing ? (
                <div className="flex items-center gap-2 text-[12.5px] text-slate-600">
                  <ScanSearch className="h-3.5 w-3.5 text-violet-600 animate-pulse" /> Analyzing image…
                </div>
              ) : (
                <div className="flex gap-1">
                  <span className="h-1.5 w-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="h-1.5 w-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="h-1.5 w-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Suggested prompts (only at the start) */}
      {messages.length <= 1 && (
        <div className="shrink-0 px-4 pb-2 flex flex-wrap gap-1.5">
          {SUGGESTED.map((p) => (
            <button
              key={p}
              onClick={() => send(p)}
              data-testid={`mguru-suggest-${p.slice(0, 8)}`}
              className="text-[11.5px] px-2.5 py-1.5 rounded-full bg-white border border-slate-200 text-slate-600 active:bg-slate-50 flex items-center gap-1"
            >
              <Sparkles className="h-3 w-3 text-violet-500" /> {p}
            </button>
          ))}
        </div>
      )}

      {/* Composer */}
      <div className="shrink-0 px-3 py-2.5 border-t border-slate-100 bg-white flex items-end gap-2 relative z-10 pb-[max(5.25rem,calc(env(safe-area-inset-bottom)+4.25rem))] md:pb-[max(3.25rem,env(safe-area-inset-bottom))]">
        <button
          onClick={() => cameraInputRef.current?.click()}
          disabled={analyzing}
          data-testid="mguru-camera-btn"
          className="w-11 h-11 shrink-0 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center disabled:opacity-50"
          aria-label="Take photo"
        >
          <Camera className="h-5 w-5" />
        </button>
        <button
          onClick={() => imageInputRef.current?.click()}
          disabled={analyzing}
          data-testid="mguru-upload-btn"
          className="w-11 h-11 shrink-0 rounded-xl bg-slate-50 text-slate-600 flex items-center justify-center disabled:opacity-50"
          aria-label="Upload image"
        >
          <Paperclip className="h-5 w-5" />
        </button>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          rows={1}
          placeholder="Ask Guru…"
          data-testid="mguru-input"
          className="flex-1 resize-none px-3 py-2.5 rounded-xl border border-slate-200 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/20 focus:border-[#2563EB] max-h-28"
        />
        <button
          onClick={() => send()}
          disabled={!input.trim() || isTyping}
          data-testid="mguru-send-btn"
          className="w-11 h-11 shrink-0 rounded-xl bg-[#2563EB] text-white flex items-center justify-center disabled:opacity-40"
          aria-label="Send"
        >
          {isTyping && !analyzing ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
        </button>
        <input ref={imageInputRef} type="file" accept="image/*" hidden onChange={(e) => { onImage(e.target.files); e.target.value = ""; }} />
        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => { onImage(e.target.files); e.target.value = ""; }} />
      </div>
    </div>
  );
}

function Bubble({ message }) {
  const isUser = message.sender === "user";
  if (isUser) {
    return (
      <div className="flex gap-2 items-start justify-end">
        <div className="max-w-[78%]">
          {message.imageUrl && (
            <div className="mb-1.5 rounded-2xl rounded-tr-sm overflow-hidden border border-slate-200 bg-white">
              <img src={message.imageUrl} alt={message.imageName || "uploaded"} className="block max-h-64 w-auto" />
            </div>
          )}
          {message.text && (
            <div className="bg-[#2563EB] text-white rounded-2xl rounded-tr-sm px-3.5 py-2.5">
              <p className="text-[13.5px] leading-relaxed whitespace-pre-wrap">{message.text}</p>
            </div>
          )}
        </div>
        <div className="h-8 w-8 rounded-lg bg-slate-200 flex items-center justify-center shrink-0">
          <User className="h-4 w-4 text-slate-600" />
        </div>
      </div>
    );
  }
  return (
    <div className="flex gap-2 items-start">
      <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center shrink-0">
        <Bot className="h-4 w-4 text-white" />
      </div>
      <div className="max-w-[82%]">
        <div className="rounded-2xl rounded-tl-sm px-3.5 py-2.5 border bg-white border-slate-200">
          <p
            className="text-[13.5px] text-slate-800 leading-relaxed whitespace-pre-wrap"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(formatMarkdown(message.text)) }}
          />
          {message.sources && message.sources.length > 0 && (
            <div className="mt-2 pt-2 border-t border-slate-100 flex flex-wrap gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mr-1">Sources:</span>
              {message.sources.map((s) => (
                <span key={s} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100">
                  <FileText className="h-2.5 w-2.5" />
                  {s}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
